import { useState } from 'react';
import { ArrowPathIcon, ArrowRightOnRectangleIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EntitlementState } from '../../services/entitlement';
import { LicensingStatus } from '../../services/licensingService';
import { signOut } from '../../services/authService';
import { APP_LOGO_SRC } from '../../lib/branding';
import { LanguageSelector } from '../common/LanguageSelector';

interface EntitlementRecoveryProps {
  entitlementState: EntitlementState;
  status: LicensingStatus | null;
  onRetry: () => Promise<LicensingStatus | null>;
}

function getMessageKey(entitlementState: EntitlementState, status: LicensingStatus | null): string {
  if (entitlementState === 'access_expired') return 'trial_expired';
  if (status?.reason === 'payment_grace_expired') return 'payment_grace_expired';
  if (status?.reason === 'subscription_inactive' || status?.billing_status === 'canceled') {
    return 'subscription_required';
  }
  if (entitlementState === 'error') return 'verification_error';
  return 'billing_issue';
}

export function EntitlementRecovery({
  entitlementState,
  status,
  onRetry,
}: EntitlementRecoveryProps) {
  const { t } = useTranslation('licensing');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const messageKey = getMessageKey(entitlementState, status);

  const handleRetry = async () => {
    setIsRefreshing(true);
    try {
      await onRetry();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await signOut();
  };

  return (
    <main className="min-h-screen bg-[var(--app-canvas)] px-5 py-6 sm:px-8">
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between">
        <img src={APP_LOGO_SRC} alt="Honey" className="h-11 w-auto" />
        <LanguageSelector variant="compact" />
      </header>

      <section
        aria-labelledby="entitlement-heading"
        className="mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-xl flex-col items-center justify-center text-center"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FFD54F] text-[#2F2F2F] shadow-sm ring-1 ring-black/5">
          <CreditCardIcon className="h-7 w-7" aria-hidden="true" />
        </div>
        <h1 id="entitlement-heading" className="mt-6 text-2xl font-bold text-[#2F2F2F] sm:text-3xl">
          {t(`blocked.${messageKey}.title`)}
        </h1>
        <p className="mt-3 max-w-lg text-sm leading-6 text-gray-600 sm:text-base">
          {t(`blocked.${messageKey}.body`)}
        </p>

        <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
          <Link
            to="/billing"
            className="inline-flex min-h-11 items-center justify-center rounded-control bg-[#FFD54F] px-5 py-2.5 text-sm font-semibold text-[#2F2F2F] shadow-sm transition-colors hover:bg-[#e6c045] focus:outline-none focus:ring-2 focus:ring-[#C79A00] focus:ring-offset-2"
          >
            {t(`blocked.${messageKey}.action`)}
          </Link>
          <button
            type="button"
            onClick={handleRetry}
            disabled={isRefreshing}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-control border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#C79A00] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('actions.retry')}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={isSigningOut}
            className="inline-flex min-h-11 items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C79A00] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
            {t('actions.sign_out')}
          </button>
        </div>
      </section>
    </main>
  );
}
